import { debounce } from 'lodash';
import { useParams } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, ChangeEvent, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { Card, Chip, Table, Avatar, Tooltip, TableBody, CardHeader, IconButton, AvatarGroup, useMediaQuery, TableContainer } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openMultiMailerDialog } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useGetListByIdQuery, useRemoveListItemsMutation, useGetListItemsPaginatedQuery } from 'src/services/lists/lists.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import AddToListModal from 'src/sections/lists/modals/add-to-list-modal';
import ListEditorModal from 'src/sections/lists/modals/list-editor-modal';
import ListsItemTableRow from 'src/sections/lists/detail/list-item-table-row';

import { TenantType } from 'src/types/enums';


const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

export default function ListsDetailView() {

  const table = useTable();

  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const { list_id: listId } = useParams();

  const theme = useTheme();

  const listEditorControl = useBoolean();
  const addToListControl = useBoolean();

  const removeConfirmControl = useBoolean();

  const tenantType = useOrgTenant();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const denseHeight = table.dense ? 56 : 56 + 20;

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [filterQuery, setFilterQuery] = useState<string>('');

  const [removeItem, { isLoading: isRemovingItem }] = useRemoveListItemsMutation();

  const { data: list, isLoading: isListLoading } = useGetListByIdQuery(listId as string,
    { skip: !listId }
  );

  const { data: listItems, isFetching: isListItemsLoading } = useGetListItemsPaginatedQuery({
    list_id: listId as string,
    page: pageOptions.page,
    per_page: pageOptions.per_page
  }, {
    skip: !list,
    refetchOnMountOrArgChange: true,
  });

  const notFound = !listItems?.count;

  const tableLabels = useMemo(
    () => {
      switch (list?.entity_type) {
        case ListEntityType.COMPANY:
          return [
            { id: 'title', label: t('connections.table.company_name') },
            { id: 'type', label: t('connections.table.type') },
            { id: 'type', label: t('connections.table.phone') },
            { id: 'type', label: t('connections.table.owner') },
            { id: 'type', label: t('connections.table.location') },
            { id: 'status', label: t('connections.table.last_activity') },
            { id: 'actions', label: '' }
          ];
        case ListEntityType.CONTACT:
          return [
            { id: 'name', label: t('contacts.table.name') },
            { id: 'email', label: t('contacts.table.email') },
            { id: 'phone', label: t('contacts.table.phone') },
            { id: 'source', label: t('contacts.table.source') },
            { id: 'type', label: t('contacts.table.type') },
            { id: 'actions', label: '' }
          ]
        case ListEntityType.CANDIDATE:
          return [

            { id: 'name', label: t('candidates.table.name') },
            { id: 'email', label: t('candidates.table.email') },
            { id: 'phone', label: t('candidates.table.phone') },
            ...tenantType === TenantType.Recruiter ? [{ id: 'availablity', label: t('candidates.table.availability') }] : [],
            { id: 'rating', label: t('candidates.table.rating') },
            { id: 'updated_at', label: t('candidates.table.updated_at') },
            { id: 'actions', label: '' }
          ];
        default:
          return [];
      }
    },
    [t, list, tenantType]
  );

  const handleFilters = useMemo(() => debounce((value: string) => {
    setFilterQuery(value);
  }, 500), [setFilterQuery]);


  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleRemoveItem = useCallback(async (itemIds: string[]) => {
    try {

      await removeItem({
        list_id: listId as string,
        data: {
          item_ids: itemIds
        }
      }).unwrap();

      table.onResetPage();
      removeConfirmControl.onFalse();
      table.onSelectAllRows(false);

      enqueueSnackbar(t('lists.api.remove_from_list.success'), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(t('lists.api.remove_from_list.default_error'), {
        variant: 'error',
      });
    }
  }, [removeItem, listId, t, table, removeConfirmControl]);

  const handleOpenMailer = useCallback(() => {
    if (!list) return;

    let templateType: EmailMessageTemplateType;
    let targetType: MailerTargetType;

    switch (list.entity_type) {
      case ListEntityType.CANDIDATE:
        templateType = EmailMessageTemplateType.CANDIDATE_ENGAGEMENT;
        targetType = MailerTargetType.CANDIDATE;
        break;
      case ListEntityType.CONTACT:
        templateType = EmailMessageTemplateType.CONTACT_ENGAGEMENT;
        targetType = MailerTargetType.CONTACT;
        break;
      default:
        return;
    }

    dispatch(openMultiMailerDialog({
      listId: list.id,
      templateType,
      targetType,
    }));
  }, [dispatch, list]);

  const handleSelectiveMail = useCallback(() => {
    if (!list) return;
    if (table.selected.length === 0) return;

    let templateType: EmailMessageTemplateType;
    let targetType: MailerTargetType;

    switch (list.entity_type) {
      case ListEntityType.CANDIDATE:
        templateType = EmailMessageTemplateType.CANDIDATE_ENGAGEMENT;
        targetType = MailerTargetType.CANDIDATE;
        break;
      case ListEntityType.CONTACT:
        templateType = EmailMessageTemplateType.CONTACT_ENGAGEMENT;
        targetType = MailerTargetType.CONTACT;
        break;
      default:
        return;
    }

    dispatch(openMultiMailerDialog({
      entities: table.selected,
      templateType,
      targetType,
    }));
  }, [dispatch, list, table.selected]);


  return (
    <Stack direction="column" spacing={2} >

      <Card variant='outlined' sx={{ pb: 2 }}>
        <CardHeader
          action={
            <Stack direction="row" alignItems="center">
              <Tooltip title={t('lists.tooltips.edit_list')}>
                <IconButton aria-label="edit" onClick={listEditorControl.onTrue}>
                  <Iconify icon="cuida:edit-outline" color="black" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('lists.tooltips.add_items')}>
                <IconButton aria-label="add-items" onClick={addToListControl.onTrue}>
                  <Iconify icon="material-symbols:post-add" color="black" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('lists.tooltips.send_bulk_mail')}>
                <IconButton aria-label="add-items" onClick={handleOpenMailer}>
                  <Iconify icon="iconoir:send-mail" color="black" />
                </IconButton>
              </Tooltip>

              {
                list && table.selected && table.selected.length > 0 && [ListEntityType.CANDIDATE, ListEntityType.CONTACT].includes(list.entity_type) && (
                  <Tooltip title={t('lists.tooltips.send_selective_bulk_mail')}>
                    <IconButton aria-label="add-items" onClick={handleSelectiveMail}>
                      <Iconify icon="fluent:mail-list-28-regular" color="black" />
                    </IconButton>
                  </Tooltip>
                )
              }

              {
                list && table.selected && table.selected.length > 0 && [ListEntityType.CANDIDATE, ListEntityType.CONTACT].includes(list.entity_type) && (
                  <Tooltip title={t('lists.tooltips.remove_items')}>
                    <IconButton aria-label="add-items" onClick={removeConfirmControl.onTrue}>
                      <Iconify icon="ix:remove-circle-filled" color="red" />
                    </IconButton>
                  </Tooltip>
                )
              }

            </Stack>
          }
          title={
            <Stack direction="row" alignItems="start" justifyContent="start" gap={1}>
              <Box>
                <TruncatedTextField
                  text={list?.name ?? 'Loading...'}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  style={{ textTransform: 'capitalize' }}
                />
              </Box>
              {
                list && (
                  <Chip label={t(`lists.entity_list_type.${list.entity_type}`)} size='small' variant='outlined' />
                )
              }

            </Stack>
          }
          subheader={
            <Stack direction="column" alignItems="start" gap={1}>
              <TruncatedTextField
                text={list?.description}
                mode="lines"
                limit={2}
                showMoreEnabled
                variant="caption"
                gutterBottom
              />

              <AvatarGroup max={5} sx={{ flexWrap: 'wrap' }} spacing="medium">
                {
                  list?.access_list.map((account) => (
                    <Tooltip key={account.id} title={`${account.first_name} ${account.last_name}`}>
                      <Avatar sizes='small' alt={account.first_name}>
                        {account?.first_name?.charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ))
                }
              </AvatarGroup>
            </Stack>
          }
        />
      </Card>

      <Card sx={{ pt: { xs: 2, sm: 'unset' } }}>

        {
          (isListItemsLoading || isListLoading) ? (
            <TableSkeleton />
          )
            :
            <>

              <TableContainer sx={{ overflow: 'unset' }}>
                <Scrollbar>
                  <Table size={table.dense ? 'small' : 'medium'}>
                    {
                      !isMobile && (
                        <TableHeadCustom
                          headLabel={tableLabels}
                          rowCount={listItems?.results.length ?? 0}
                          numSelected={table.selected.length}
                          onSelectAllRows={(checked) =>
                            table.onSelectAllRows(
                              checked,
                              (listItems?.results ?? []).map((row) => row.id) as string[]
                            )
                          }
                        />
                      )
                    }

                    <TableBody >
                      {listItems?.results?.map((row) =>
                        <ListsItemTableRow
                          key={row.id}
                          entityType={list?.entity_type as ListEntityType}
                          row={row}
                          selected={table.selected.includes(row.id as string)}
                          onSelectRow={() => table.onSelectRow(row.id as string)}
                          onRemoveItem={() => handleRemoveItem([row.id as string])}
                        />)}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(table.page, table.rowsPerPage, listItems?.count ?? 0)}
                      />

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>

              <TablePaginationCustom
                count={listItems?.count ?? 0}
                page={pageOptions.page}
                rowsPerPage={pageOptions.per_page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </>
        }

        <AddToListModal open={addToListControl.value} onClose={addToListControl.onFalse} listId={listId as string} />
        <ListEditorModal open={listEditorControl.value} onClose={listEditorControl.onFalse} entityType={list?.entity_type as ListEntityType} listId={list?.id} />

        <ConfirmDialog
          open={removeConfirmControl.value}
          onClose={removeConfirmControl.onFalse}
          title={t('lists.actions.remove_from_list')}
          content={t('lists.actions.remove_from_list_confirm', {
            count: table.selected.length
          })}
          action={
            <LoadingButton
              variant="contained"
              color="error"
              loading={isRemovingItem}
              onClick={() => handleRemoveItem(table.selected)}
            >
              {t('common.remove')}
            </LoadingButton>
          }
        />
      </Card>

    </Stack>
  );
}
